import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ConfigService } from 'src/app/services/config.service';

interface DraftLine {
  product_id: number | null;
  product_sku: string | null;
  product_name?: string | null;
  sku?: string | null;
  description?: string | null;
  quantity: number | null;
  cost: number | null;
  uom_id: number | null;
  purchase_order_id: number | null;
  po_line_id: number | null;
  flags: string[];
  include?: boolean;
  /** What the document said for this line (the SKU as written, also when it matched nothing). */
  source?: { sku?: string | null; description?: string | null; quantity?: number | null } | null;
}

/**
 * WizeHelp > "ASN from a document": the AI agent reads a supplier's packing list / PO into a draft, the user checks
 * it here and creates the ASN through the normal ASN API (POST /api/asn — same validation as the ASN form).
 * Self-contained: the ASN pages are not changed. Nothing is created until the user clicks "Create ASN".
 */
@Component({
  selector: 'app-agent-asn-intake',
  templateUrl: './agent-asn-intake.component.html',
  styleUrls: ['./agent-asn-intake.component.scss'],
})
export class AgentAsnIntakeComponent implements OnInit {
  readonly acceptedTypes = '.pdf,.png,.jpg,.jpeg,.webp,.csv,.txt';
  readonly maxFileMb = 10;

  step: 'upload' | 'review' | 'done' = 'upload';
  clientList: any[] = [];
  clientId: number | null = null;
  file: File | null = null;
  isLoadingClients = false;
  isReading = false;
  isCreating = false;
  errorMessage = '';

  // review
  draft: any = null;
  lines: DraftLine[] = [];
  unmatched: DraftLine[] = [];
  vendorList: any[] = [];
  warehouseList: any[] = [];
  vendorId: number | null = null;
  warehouseId: number | null = null;
  expectedDate = '';
  referenceNo = '';
  notes = '';

  // done
  created: { id: number; asn_no: string } | null = null;

  constructor(
    public dialogRef: MatDialogRef<AgentAsnIntakeComponent>,
    private service: ConfigService,
    private router: Router,
  ) {}

  async ngOnInit(): Promise<void> {
    this.isLoadingClients = true;
    const response: any = await this.service.getApi('/api/client/lookup', {}, '', 'auth_service');
    this.clientList = response?.data || [];
    if (this.clientList.length === 1) {
      this.clientId = this.clientList[0].id;
    }
    this.isLoadingClients = false;
  }

  get canRead(): boolean {
    return !!this.file && !!this.clientId && !this.isReading;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    this.errorMessage = '';
    if (file && file.size > this.maxFileMb * 1024 * 1024) {
      this.errorMessage = `The file is larger than ${this.maxFileMb} MB.`;
      this.file = null;
      input.value = '';
      return;
    }
    this.file = file;
  }

  /** Step 1 → 2: the agent reads the document (no ASN is created). */
  async read(): Promise<void> {
    if (!this.canRead) {
      return;
    }
    this.isReading = true;
    this.errorMessage = '';
    const response: any = await this.service.uploadFiles('/api/agent/intake/asn', { file: this.file, client_id: this.clientId }, 'wms_service');
    this.isReading = false;
    if (!response?.data?.lines) {
      this.errorMessage = response?.error?.message || response?.message || "I couldn't read this document — please enter the ASN manually.";
      return;
    }
    await this.startReview(response.data);
  }

  async startReview(draft: any): Promise<void> {
    this.draft = draft;
    const all: DraftLine[] = (draft.lines || []).map((l: DraftLine) => ({ ...l, include: !!l.product_id }));
    this.lines = all.filter((l) => l.product_id);
    this.unmatched = all.filter((l) => !l.product_id);
    this.vendorId = draft.vendor_id ?? null;
    this.expectedDate = draft.expected_delivery_date || '';
    this.referenceNo = String(draft.reference_no || draft.po_no || '').substring(0, 50);
    this.notes = draft.notes || '';
    const [vendors, warehouses]: any[] = await Promise.all([
      this.service.getApi('/api/vendors/lookup?client_id=' + this.clientId, {}),
      this.service.getApi('/api/warehouse/lookup?client_id=' + this.clientId, {}),
    ]);
    this.vendorList = vendors?.data || [];
    this.warehouseList = warehouses?.data || [];
    if (this.warehouseList.length === 1) {
      this.warehouseId = this.warehouseList[0].id;
    }
    this.step = 'review';
  }

  /** An unmatched line in the words of the document. */
  unmatchedLabel(line: DraftLine): string {
    const name = line.source?.sku || line.sku || line.source?.description || line.description || '?';
    return `${name} × ${line.quantity ?? line.source?.quantity ?? '?'}`;
  }

  flagLabel(flag: string): string {
    const labels: Record<string, string> = {
      unknown_sku: 'SKU not found',
      low_confidence: 'hard to read — check it',
      missing_quantity: 'quantity missing',
      quantity_differs_from_po: 'differs from the PO',
      not_on_po: 'not on the PO',
    };
    return labels[flag] || flag;
  }

  get includedLines(): DraftLine[] {
    return this.lines.filter((l) => l.include);
  }

  /** What still blocks "Create ASN" (shown under the button). */
  get problems(): string[] {
    const out: string[] = [];
    if (!this.vendorId) {
      out.push('Choose the supplier.');
    }
    if (!this.warehouseId) {
      out.push('Choose the warehouse.');
    }
    if (!this.includedLines.length) {
      out.push('Keep at least one line.');
    }
    if (this.includedLines.some((l) => !l.quantity || l.quantity <= 0)) {
      out.push('Every line needs a quantity above 0.');
    }
    if (this.includedLines.some((l) => l.cost === null || l.cost === undefined || l.cost < 0)) {
      out.push('Every line needs a cost (0 or more).');
    }
    if (this.includedLines.some((l) => !l.uom_id)) {
      out.push('A matched SKU has no unit of measure — add it on the ASN page instead.');
    }
    return out;
  }

  /** Step 2 → 3: the normal ASN API creates it (supplier / warehouse / SKUs validated for the client there). */
  async create(): Promise<void> {
    if (this.problems.length || this.isCreating) {
      return;
    }
    this.isCreating = true;
    this.errorMessage = '';
    const date = this.expectedDate ? `${this.expectedDate} 00:00:00` : '';
    const payload: any = {
      client_id: this.clientId,
      vendor_id: this.vendorId,
      asn_to: this.warehouseId,
      expected_delivery_date: date,
      reference_no: this.referenceNo,
      notes: this.notes,
      // Same line keys the ASN form sends. Empty strings, not null: FormData would send "null".
      products: this.includedLines.map((l) => ({
        id: l.po_line_id ?? '',
        product_id: l.product_id,
        quantity: l.quantity,
        uom_id: l.uom_id,
        cost: l.cost ?? 0,
        purchase_order_id: l.purchase_order_id ?? '',
        requested_delivery_date: date || new Date().toISOString().slice(0, 10) + ' 00:00:00',
        notes: '',
        attachments: [],
      })),
    };
    const response: any = await this.service.postFormData('/api/asn', payload, undefined, 'wms_service');
    this.isCreating = false;
    if (response?.data?.id) {
      this.created = { id: response.data.id, asn_no: response.data.asn_no };
      this.step = 'done';
      return;
    }
    this.errorMessage = response?.error?.message || response?.message || 'The ASN could not be created.';
  }

  openAsn(): void {
    if (this.created) {
      this.dialogRef.close(this.created);
      this.router.navigate(['/inbound/asn/detail', this.created.id]);
    }
  }

  back(): void {
    this.step = 'upload';
    this.errorMessage = '';
  }

  close(): void {
    this.dialogRef.close(this.created);
  }
}
